import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Button,
  ButtonVariantOption,
  SubmitButton,
} from "../../../components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import React, { useState } from "react";
import { useValidator } from "../hooks/useValidator";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import {
  preventNonNumericValueWhenPaste,
  preventNonNumericValueWithoutBackspaceWhenKeyDown,
} from "@/utils/utils";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { type HandOffMessage } from "@/types/handOffMessage";
import { isAxiosError } from "axios";
import { useFeatureFlags } from "@/configs/featureFlag";
import { Label } from "@/components/Label";

interface UploadResponse {
  nonFieldErrors: string[];
}

// エラーレスポンスの型を追加
interface ErrorResponse {
  errorCode: string;
}

export const AllMattersPDFUpload: React.FC = () => {
  // 識別番号
  const [identifierNumber, setIdentifierNumber] = useState<string>("");
  // 識別名
  const [identifierName, setIdentifierName] = useState<string>("");

  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = React.useState(false);

  const [fileToUpload, setFileToUpload] = React.useState<File | null>(null);

  // 実行ボタンの有効/無効を決定するフラグ
  const [executeButtonDisabled, setExecuteButtonDisabled] =
    useState<boolean>(true);

  // バリデーションのためのフックを導入する
  const validator = useValidator();
  // ファイルのバリデーションエラー状態を追加
  const [fileError, setFileError] = useState<boolean>(false);

  const navigate = useNavigate();
  const { apiClient } = useApiClient();
  const { mypageTop } = useFeatureFlags();

  // loading状態を追跡するステート変数を追加
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);

  const handleFileUploadButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    if (fileToUpload) {
      const formData = new FormData();
      formData.append("file", fileToUpload);

      // 識別番号が空の場合は6桁のランダムな数字を生成
      const taskId = identifierNumber.trim() || generateRandom6DigitNumber();
      formData.append("task_id", taskId);

      formData.append("identification_name", identifierName);
      apiClient
        .post("/all-matters-analyze/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          // 成功時の処理
          const handOffMessage: HandOffMessage = {
            command: "ALL_MATTERS_SHOW_ACCEPT_MESSAGE",
          };
          navigate("/allmatters/list", {
            replace: true,
            state: { handOffMessage },
          });
        })
        .catch((e) => {
          cancelLoading();
          // レスポンスの結果が502の時はエラーを無視する
          if (
            isAxiosError<UploadResponse | ErrorResponse>(e) &&
            e.response?.status != null &&
            e.response?.status > 300 &&
            e.response?.status !== 502
          ) {
            console.error(e);
            let isSetMessage = false;
            if (e.response?.status === 400) {
              const data = e.response?.data;
              // error_codeがある場合のエラーハンドリング
              if ("errorCode" in data) {
                const errorCode = data.errorCode;
                // エラーコードに応じたメッセージを表示
                switch (errorCode) {
                  case "FILE_COUNT_EXCEEDED":
                    toast.error("ファイル数が超過しています");
                    break;
                  default:
                    toast.error(
                      `全部事項解析ファイルのアップロードに失敗しました`
                    );
                }
                isSetMessage = true;
              } else if (
                data.nonFieldErrors &&
                data.nonFieldErrors.length > 0
              ) {
                // 既存のnonFieldErrorsのハンドリング
                toast.error(data.nonFieldErrors[0]);
                isSetMessage = true;
              }
            }
            if (!isSetMessage) {
              toast.error("全部事項解析ファイルのアップロードに失敗しました");
            }
          }
        });
    }
  };

  const generateRandom6DigitNumber = (): string => {
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += Math.floor(Math.random() * 10).toString();
    }
    return result;
  };

  return (
    <PagePaper>
      <form>
        {/* ページタイトル */}
        <Stack direction={"row"} justifyContent="space-between">
          <Box>
            <Box sx={{ display: "flex", mb: 0 }}>
              <PictureAsPdfIcon sx={{ mr: 1 }} fontSize="large" />
              <PageTitle>全部事項データ化（テスト版）</PageTitle>
            </Box>
            <Typography sx={{ ml: 5, mb: 3 }}>
              Zipファイルをアップロードしてください
            </Typography>
          </Box>
          <Box sx={{ my: 2, mx: 4 }}>
            <Button
              label={"実行する"}
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
              disabled={executeButtonDisabled || !fileToUpload}
              onClick={() => {
                const results = [
                  validator.identifierName.validate(identifierName),
                ];

                // ファイルのバリデーションチェック
                const isFileValid = fileToUpload !== null;
                setFileError(!isFileValid);

                // resultsの全ての値がtrueかつファイルが選択されているならvalidation通過とみなす
                if (results.every((value) => value) && isFileValid) {
                  setOpenModalFlag(true);
                }
              }}
            />
          </Box>
        </Stack>
        <SectionPaper>
          <Grid sx={{ m: 2 }}>
            <Grid container spacing={2} justifyContent={"center"}>
              <Grid item xs={6}>
                <Label required={false}>識別番号</Label>
                <TextField
                  error={validator.identifierNumber.state.error}
                  label="識別番号"
                  helperText={
                    validator.identifierNumber.state.error
                      ? validator.identifierNumber.state.helperTextWhenError
                      : "このファイルにわかりやすい番号を付けてください（任意）"
                  }
                  required={false}
                  value={identifierNumber}
                  onKeyDown={preventNonNumericValueWithoutBackspaceWhenKeyDown}
                  onPaste={preventNonNumericValueWhenPaste}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setIdentifierNumber(event.target.value);
                    // 任意項目なのでバリデーションは空文字でもOK
                    validator.identifierNumber.validate(event.target.value);
                  }}
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={6}>
                <Label required={true}>識別名</Label>
                <TextField
                  error={validator.identifierName.state.error}
                  label="識別名"
                  helperText={
                    validator.identifierName.state.error
                      ? validator.identifierName.state.helperTextWhenError
                      : "このファイルにわかりやすい名前を付けてください"
                  }
                  value={identifierName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setIdentifierName(event.target.value);
                    validator.identifierName.validate(event.target.value);
                  }}
                  sx={{ width: "100%" }}
                />
              </Grid>
            </Grid>
            <Container
              maxWidth={false}
              disableGutters
              sx={{
                display: "flex",
                flexDirection: "column",
                border: "2px dashed",
                borderRadius: "2px",
                borderColor: fileError ? "error.main" : "black",
                mt: 3,
              }}
            >
              <Grid container spacing={2} sx={{ my: 5 }}>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Typography sx={{ lineHeight: 2 }}>
                    アップロードボタンを押してZipファイルを選択してください
                  </Typography>
                  {fileError && (
                    <Typography color="error">
                      ファイルを選択してください
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <FileUploadButton
                      variant={ButtonVariantOption.Contained}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        // ファイルが選択されているときのみ確認画面へボタンを有効にする
                        const files = event.target.files ?? [];
                        if (files.length > 0) {
                          setFileToUpload(files[0]);
                          setFileError(false);
                          setExecuteButtonDisabled(false);
                        } else {
                          setFileToUpload(null);
                          setExecuteButtonDisabled(true);
                          if (fileToUpload === null) {
                            setOpenModalFlag(false);
                          }
                        }
                      }}
                      accept={".zip"}
                      label={"アップロード"}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography textAlign={"center"}>
                    {fileToUpload ? `ファイル名:${fileToUpload?.name}` : ""}
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </Grid>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            href={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>

      {/* 確認のモーダル画面 */}
      <Dialog
        open={openModalFlag}
        onClose={() => {
          setOpenModalFlag(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"確認画面"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            全部事項解析を依頼します
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SubmitButton
            variant={ButtonVariantOption.Contained}
            onClick={(event, cancelLoading) => {
              setIsAnalyzing(true);
              const wrappedCancelLoading = (): void => {
                setIsAnalyzing(false);
                cancelLoading();
              };
              handleFileUploadButtonClick(event, wrappedCancelLoading);
            }}
            label={"解析依頼する"}
            timeout={120000}
            loadingIndicator={
              <Typography variant="button">解析依頼中...</Typography>
            }
          />
          <Button
            variant={ButtonVariantOption.Outlined}
            onClick={() => {
              setOpenModalFlag(false);
            }}
            label={"戻る"}
            disabled={isAnalyzing} // 解析依頼中は戻るボタンを無効化
          />
        </DialogActions>
      </Dialog>
    </PagePaper>
  );
};
